import { useMelwaysLink } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Stack,
  TextLink,
  Text,
  IconInfo,
  TooltipRenderer,
  Inline,
  Heading,
} from 'braid-design-system';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import AnimatedHeight from 'src/components/AnimatedHeight/AnimatedHeight';
import {
  isBrowserAnalyticsFacade,
  useAnalyticsFacade,
} from 'src/modules/AnalyticsFacade';
import { useIsHomepageGenAIChatEnabled } from 'src/store/featureFlags/hooks';
import { saveSolReferencesForRecsAction } from 'src/store/results';

import { CareerFeedAIAssistant } from '../CareerFeedAIAssistant/CareerFeedAIAssistant';
import { CareerFeedAIAssistantEmptyState } from '../CareerFeedAIAssistant/CareerFeedAIAssistantEmptyState/CareerFeedAIAssistantEmptyState';
import { useCareerFeedAIAssistant } from '../CareerFeedAIAssistant/hooks/useCareerFeedAIAssistant';
import { RecsInterceptSurvey } from '../RecsInterceptSurvey/RecsInterceptSurvey';
import { CardsStack } from '../SharedComponent/CardsStack/CardsStack';
import { ErrorState } from '../SharedComponent/ErrorState/ErrorState';
import { MessageWrapper } from '../SharedComponent/MessageWrapper/MessageWrapper';
import { useSignedInDashboardContext } from '../SignedInDashboardContextProvider';
import {
  RECOMMENDATIONS_LIMIT,
  type RecommendedJobs,
} from '../hooks/useHomeRecommendations';

import translations from './.vocab';
import { LoadingRecommendations } from './LoadingRecommendations';
import { RecommendedJobCard } from './RecommendedJobCard';

const LOADING_SKELETON_COUNT = RECOMMENDATIONS_LIMIT;
export const JOBS_BEFORE_SURVEY_LIMIT = 20;
const JOBS_BEFORE_CAREER_FEED_LIMIT = 3;

export const RECOMMENDATIONS_CONTENT_DATA_AUTOMATION = 'recommendedJobs';

const NoRecommendations = () => {
  const profileLink = useMelwaysLink({
    path: '/profile/me',
  });
  const analyticsFacade = useAnalyticsFacade();

  const profileLinkOnClick = useCallback(() => {
    analyticsFacade.linkClicked({
      href: profileLink,
      linkName: 'dashboard-recommended-jobs-profile',
    });
  }, [analyticsFacade, profileLink]);

  const { t } = useTranslations(translations);

  return (
    <MessageWrapper>
      {t('Update your profile or start searching', {
        Link: (children) => (
          <TextLink
            weight="weak"
            target="_blank"
            href={profileLink}
            onClick={profileLinkOnClick}
          >
            {children}
          </TextLink>
        ),
      })}
    </MessageWrapper>
  );
};

const useTrackingRecommendedJobs = (
  recommendedJobs: RecommendedJobs | null | undefined,
) => {
  const [isViewedEventSent, setIsViewedEventSent] = useState(false);
  const analyticsFacade = useAnalyticsFacade();
  const dispatch = useDispatch();

  useEffect(() => {
    if (recommendedJobs && !isViewedEventSent) {
      if (isBrowserAnalyticsFacade(analyticsFacade)) {
        analyticsFacade.recommendedJobsImpression({
          solMetadata: recommendedJobs.solMetadata,
        });
      }
      setIsViewedEventSent(true);
    }
  }, [
    analyticsFacade,
    isViewedEventSent,
    recommendedJobs,
    setIsViewedEventSent,
  ]);

  useEffect(() => {
    const globalJobs = recommendedJobs?.globalJobs ?? [];
    if (globalJobs.length) {
      dispatch(saveSolReferencesForRecsAction(globalJobs, 'loggedInHomeRecs'));
    }
  }, [dispatch, recommendedJobs]);
};

const RecommendationsContent = () => {
  const isHomepageGenAIChatEnabled = useIsHomepageGenAIChatEnabled();
  const { persistedCandidateChatContext } = useCareerFeedAIAssistant();

  const isRenderingGenAIRecs = Boolean(
    isHomepageGenAIChatEnabled && persistedCandidateChatContext,
  );

  const {
    recommendationsResult: { jobs, loading, error },
  } = useSignedInDashboardContext();

  if (error) {
    return <ErrorState section="recommendedJobs" />;
  }

  if (!jobs.length && !loading) {
    if (isRenderingGenAIRecs) {
      return <CareerFeedAIAssistantEmptyState />;
    }

    return <NoRecommendations />;
  }

  const recommendationsList = jobs.map((job, index) => (
    <RecommendedJobCard
      key={`recommendedJob_${job.id}`}
      testAutomation={{
        jobCardLink: `recommendedJobLink_${job.id}`,
      }}
      jobInfo={job}
      position={index + 1}
      isFeaturedOrDisplayKeyType="loggedInHomeRecs"
    />
  ));

  if (jobs.length >= JOBS_BEFORE_SURVEY_LIMIT) {
    recommendationsList.splice(
      JOBS_BEFORE_SURVEY_LIMIT,
      0,
      <RecsInterceptSurvey key="recsInterceptSurvey" />,
    );
  }

  return (
    <CardsStack data={{ automation: RECOMMENDATIONS_CONTENT_DATA_AUTOMATION }}>
      {loading ? (
        <LoadingRecommendations
          length={JOBS_BEFORE_CAREER_FEED_LIMIT}
          keyPrefix="recsOnTop"
        />
      ) : (
        recommendationsList.slice(0, JOBS_BEFORE_CAREER_FEED_LIMIT)
      )}
      {isHomepageGenAIChatEnabled && <CareerFeedAIAssistant />}
      {loading ? (
        <LoadingRecommendations
          length={LOADING_SKELETON_COUNT - JOBS_BEFORE_CAREER_FEED_LIMIT}
          keyPrefix="recsOnBottom"
        />
      ) : (
        recommendationsList.slice(JOBS_BEFORE_CAREER_FEED_LIMIT)
      )}
    </CardsStack>
  );
};

export const RECOMMENDED_JOBS_SPACE = 'small';

export const Recommendations = () => {
  const { t } = useTranslations(translations);

  const {
    recommendationsResult: { rawRecommendedJobs, loading },
  } = useSignedInDashboardContext();

  useTrackingRecommendedJobs(rawRecommendedJobs);

  const { messages: aiAssistantMessage, progress: aiAssistantProgress } =
    useCareerFeedAIAssistant();

  return (
    <Stack space={RECOMMENDED_JOBS_SPACE}>
      <Inline space="xxsmall" alignY="center">
        <Heading level="3">{t('Recommended jobs')}</Heading>
        <TooltipRenderer
          id=""
          tooltip={
            <Text align="center" size="small">
              {t('Based on your profile, job views and applications.')}
            </Text>
          }
        >
          {({ triggerProps }) => (
            <Box aria-label="Info" {...triggerProps}>
              <IconInfo size="xsmall" />
            </Box>
          )}
        </TooltipRenderer>
      </Inline>

      <AnimatedHeight
        changeKeys={[
          loading,
          rawRecommendedJobs,
          aiAssistantMessage,
          aiAssistantProgress,
        ]}
      >
        <RecommendationsContent />
      </AnimatedHeight>
    </Stack>
  );
};
